import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  User,
  MapPin,
  Phone,
  Mail,
  Loader2,
  ChevronRight,
  Tag,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const ClientsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);
  
  // Re-fetch clients when category changes (if filtering server-side)
  useEffect(() => {
    if (!loading) {
      fetchClients();
    }
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [clientsRes, categoriesRes] = await Promise.all([
        api().get('/clients'),
        api().get('/admin/client-categories'),
      ]);
      setClients(clientsRes.data.clients);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category_id=${selectedCategory}` : '';
      const response = await api().get(`/clients${params}`);
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.service_address?.toLowerCase().includes(search) ||
      client.phone?.includes(search)
    );
  });

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-emerald-50 text-emerald-700',
      inactive: 'bg-zinc-100 text-zinc-700',
      prospect: 'bg-blue-50 text-blue-700',
    };
    return colors[status] || 'bg-zinc-100 text-zinc-700';
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Clients</h1>
          <p className="text-zinc-500 mt-1">Manage your client database</p>
        </div>
        <Link to="/clients/new">
          <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="new-client-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Client
          </Button>
        </Link>
      </div>

      {/* Search and Filter */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search clients by name, email, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-zinc-400 hidden md:block" />
              <Select 
                value={selectedCategory} 
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger className="w-full md:w-[180px]" data-testid="category-filter-select">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-zinc-400" />
                    <SelectValue placeholder="All Categories" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Active filter indicator */}
          {selectedCategory !== 'all' && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {categories.find(c => c.id === selectedCategory)?.name || 'Category'}
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className="ml-1 hover:bg-zinc-200 rounded-full p-0.5"
                  data-testid="clear-category-filter"
                >
                  ×
                </button>
              </Badge>
              <span className="text-xs text-zinc-500">{filteredClients.length} clients</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : filteredClients.length === 0 ? (
        <Card className="border-zinc-100">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
              <User className="h-8 w-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-1">No clients found</h3>
            <p className="text-zinc-500">
              {searchTerm ? 'Try adjusting your search' : 'Add your first client to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="border-zinc-100 hover:border-zinc-200 transition-colors cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
              data-testid={`client-row-${client.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-medium text-zinc-900">{client.name}</h3>
                      <Badge className={getStatusBadge(client.status)}>
                        {client.status}
                      </Badge>
                      {client.category_name && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {client.category_name}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-zinc-500">
                      {client.service_address && (
                        <p className="flex items-center gap-2 truncate">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{client.service_address}</span>
                        </p>
                      )}
                      <div className="flex items-center gap-4 flex-wrap">
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            {client.phone}
                          </span>
                        )}
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {client.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-400 flex-shrink-0 ml-4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
